import type { OTCDealData } from '@src/types/OTC';
import {
  isUnsecuredLoanSecurityType,
  resolveLoanCollateralAssetDisplay,
  UNSECURED_LOAN_COLLATERAL_DISPLAY,
} from '@src/utils/OTC/loanSecurity';
import { AgGridCurrency, isFixedRateLoanSecurity, isFloatingRateLoanSecurity, Text, type Security } from '@talos/kyoko';
import type { ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { get } from 'lodash-es';

export function getLoanSecurityFromDeal(
  data: OTCDealData | undefined,
  securitiesBySymbol: Map<string, Security> | undefined
) {
  if (!data || !securitiesBySymbol) {
    return undefined;
  }
  const symbol = get(data, 'Symbol');
  const security = symbol ? securitiesBySymbol.get(symbol) : undefined;
  return isFixedRateLoanSecurity(security) || isFloatingRateLoanSecurity(security) ? security : undefined;
}

export function isUnsecuredLoanDeal(
  data: OTCDealData | undefined,
  securitiesBySymbol: Map<string, Security> | undefined
): boolean {
  const security = getLoanSecurityFromDeal(data, securitiesBySymbol);
  return isUnsecuredLoanSecurityType(security?.ProductDetails?.LoanSecurityType);
}

export function getLoanCollateralAssetDisplayValue(params: ValueGetterParams<OTCDealData>): string | undefined {
  const security = getLoanSecurityFromDeal(params.data, params.context?.current?.securitiesBySymbol);
  return resolveLoanCollateralAssetDisplay(
    security?.ProductDetails?.LoanSecurityType,
    security?.ProductDetails?.CollateralAsset
  );
}

/**
 * Secured loans: currency icon + CollateralAsset symbol.
 * Unsecured loans: plain "Unsecured" text (colorTextDefault / gray-090), even if CollateralAsset is present.
 */
export function LoanCollateralAssetCell(params: ICellRendererParams<OTCDealData>) {
  const security = getLoanSecurityFromDeal(params.data, params.context?.current?.securitiesBySymbol);
  if (isUnsecuredLoanSecurityType(security?.ProductDetails?.LoanSecurityType)) {
    return (
      <Text color="colorTextDefault" data-testid="loan-collateral-asset-unsecured">
        {UNSECURED_LOAN_COLLATERAL_DISPLAY}
      </Text>
    );
  }

  return (
    <AgGridCurrency {...params} value={security?.ProductDetails?.CollateralAsset} showDescription={false} showIcon />
  );
}
